import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, PenLine, ShieldCheck, Camera } from "lucide-react";
import JsBarcode from "jsbarcode";
import { SignaturePad } from "@/components/SignaturePad";

function Barcode({ value, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (value && ref.current) {
      try {
        JsBarcode(ref.current, value, { format: "CODE128", displayValue: true, fontSize: 13, height: 44, margin: 0, background: "transparent", lineColor: "#1f1f1f" });
      } catch (e) { /* invalid barcode characters */ }
    }
  }, [value]);
  if (!value) return null;
  return <svg ref={ref} data-testid={testid} />;
}

const CONTRACT_PRINT_CSS = `
@page { margin: 7mm; }
@media print {
  body { background: #ffffff !important; color: #000000 !important; }
  .no-print, footer { display: none !important; }
  .contract-shell { padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
  .print-area { box-shadow: none !important; border: none !important; border-radius: 0 !important; padding: 8px !important; background: #ffffff !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; font-size: 11px !important; }
  .print-area * { color: #000000 !important; }
  .print-area img { max-height: 64px !important; width: auto !important; }
  .print-area [data-testid="contract-vin-barcode"] { max-height: none !important; max-width: none !important; height: 48px !important; width: 200px !important; }
  .print-area .text-sm { font-size: 11px !important; }
  .print-area .text-xs { font-size: 9.5px !important; }
  .print-area .p-8, .print-area .md\\:p-12 { padding: 8px !important; }
  .print-area .pb-6 { padding-bottom: 8px !important; }
  .print-area .mb-8 { margin-bottom: 8px !important; }
  .print-area .mt-8 { margin-top: 8px !important; }
  .print-area .space-y-4 > * + * { margin-top: 6px !important; }
  .avoid-break { page-break-inside: avoid; }
}
`;

const TERMS = [
  "The renter and all additional drivers must be 25 years or older and hold a valid driver's license for at least 1 year. A photo of each license is required at booking, and additional drivers must be registered with Easy Drive Car Rental before operating the vehicle.",
  "Only registered drivers may operate the vehicle. If an unauthorized driver is found or caught driving the vehicle, this contract is cancelled immediately without refund and the vehicle will be recovered.",
  "All cancellations are subject to a 20% cancellation charge, regardless of notice period. Cancellations within 24 hours of the scheduled pickup time are NON-REFUNDABLE. No-shows forfeit the full rental amount.",
  "Reschedules: the first reschedule is free when requested more than 24 hours before pickup; a second reschedule carries a 20% fee; a third reschedule request automatically cancels the booking under the standard cancellation policy.",
  "Fuel: the vehicle leaves at the fuel level recorded at pickup (Full, 3/4, 1/2 or 1/4) and must be returned at the same level. Vehicles returned below the recorded level are refueled at a rate of $8.00 per gallon, charged to the final invoice.",
  "Vehicles are to be returned clean with no waste products left inside — a $30.00 cleaning fee will be added if returned dirty. Smoking is strictly prohibited in vehicles — a penalty of $150.00 will be charged.",
  "Third-Party Liability Insurance is included in every rental at no additional charge, with a $500.00 deductible per incident payable by the renter. The renter is responsible for the first $500.00 of any damage claim. Insurance is voided by off-road use, driving under the influence, or unregistered drivers.",
  "Vehicle burglary, theft or vandalism must be reported immediately to the nearest local police station and to Easy Drive Car Rental. These are NOT covered by insurance and are the responsibility of the renter to reimburse to the company.",
  "All accidents must be reported immediately to local officials at the Traffic Police Station and to Easy Drive Car Rental.",
  "Damage occurring during the rental is subject to loss of the security deposit plus additional charges for repairs. No repairs are to be done without the written consent of the rental company.",
  "If the vehicle experiences mechanical failure, battery failure or tire damage, contact the rental company immediately for instructions — no repairs are permitted.",
  "Vehicles must be returned to the agreed pick-up/drop-off point at the time stated in this agreement. Late returns incur $25.00 per hour. Extensions must be added to the existing booking before the scheduled return time, otherwise late fees are charged to the final invoice.",
  "Drive on the LEFT-hand side of the road in accordance with Bahamian traffic laws. Vehicles must remain on New Providence and Paradise Island. Off-road and beach driving is strictly prohibited. Seat belts are mandatory for all occupants.",
  "The vehicle is to be used solely for leisure and light duties. No modifications or changes may be made to the rental vehicle during the rental period.",
  "Toll pass usage for Paradise Island bridge access carries an $8.99 service fee per rental period plus $3.00 per trip over the bridge; trips are recorded during the rental and charged at the end of the rental agreement.",
  "Keys: loss or lockout is the renter's responsibility. Replacement of a lost key fob is charged at $250.00 or the dealer's replacement price, whichever is higher.",
  "A security deposit of $150.00 must be paid on all vehicles — no exceptions.",
  "The renter authorizes Easy Drive Car Rental to charge the payment method on file for any post-rental charges due under this agreement, including tolls, damage, late return, fuel, cleaning and administrative fees. A receipt is provided for every charge.",
  "Traffic and parking violations incurred during the rental period are the renter's responsibility. Any fines or penalties received by Easy Drive Car Rental will be charged to the renter plus a $25.00 administrative fee per violation.",
  "All rental charges are subject to 10% Bahamas VAT and a 5% processing fee, which are itemized on the booking invoice.",
  "Easy Drive Car Rental may substitute the reserved vehicle with another vehicle of equal or greater class. When a complimentary upgrade is provided, the original booked rate remains in effect and any savings shown are complimentary and provided for reference only.",
  "If the vehicle is impounded, towed or rendered unavailable due to the renter's fault or negligence, the renter is responsible for all recovery, towing and storage costs plus loss of use at the booked daily rate until the vehicle is returned to service.",
  "Easy Drive Car Rental is not responsible for personal property left in the vehicle during or after the rental period.",
  "In the event of a hurricane or severe weather warning, the renter must follow all official guidance and any instructions from Easy Drive Car Rental to secure or return the vehicle.",
  "For airport pickups and drop-offs, the renter is responsible for all airport parking fees, including any parking fees incurred at the time of return.",
];

export default function Contract() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [signer, setSigner] = useState("");
  const [initials, setInitials] = useState("");
  const [signatureImage, setSignatureImage] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [address, setAddress] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [vehiclePhoto, setVehiclePhoto] = useState(null);
  const [keyPhoto, setKeyPhoto] = useState(null);
  const [fuelPhoto, setFuelPhoto] = useState(null);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [signing, setSigning] = useState(false);
  const [addlDrivers, setAddlDrivers] = useState([]);
  const [addlUploads, setAddlUploads] = useState({});

  const load = (emailToUse) => {
    const stored = localStorage.getItem(`booking_email_${bookingId}`) || "";
    const em = emailToUse ?? stored;
    api
      .get(`/bookings/${bookingId}/contract`, { params: em ? { email: em } : {} })
      .then((r) => {
        setData(r.data);
        setError("");
        const bk = r.data?.booking || {};
        setAddlDrivers(Array.from({ length: bk.additional_drivers || 0 }, (_, i) => {
          const d = (bk.additional_driver_details || [])[i] || {};
          return { name: d.name || d.full_name || "", license_number: "" };
        }));
      })
      .catch((e) => setError(errMsg(e)));
  };

  useEffect(() => {
    const emParam = new URLSearchParams(window.location.search).get("email");
    if (emParam) localStorage.setItem(`booking_email_${bookingId}`, emParam.trim().toLowerCase());
    load(emParam || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const sign = async () => {
    if (!signer.trim() || !initials.trim() || !licenseNumber.trim() || !address.trim())
      return toast.error("Fill in your name, initials, license/passport number and address to sign");
    if (!signatureImage)
      return toast.error("Draw your signature in the signature box to sign");
    const needAddl = data?.booking?.additional_drivers || 0;
    for (let i = 0; i < needAddl; i++) {
      const d = addlDrivers[i] || {};
      if (!d.name?.trim() || !d.license_number?.trim())
        return toast.error(`Fill in additional driver ${i + 1} full name and license number`);
      if (!addlUploads[i])
        return toast.error(`Upload the driver's license photo for additional driver ${i + 1}`);
    }
    setSigning(true);
    try {
      const stored = localStorage.getItem(`booking_email_${bookingId}`) || email;
      await api.post(`/bookings/${bookingId}/contract/sign`, {
        signer_name: signer.trim(),
        initials: initials.trim(),
        signature_image: signatureImage,
        license_number: licenseNumber.trim(),
        address: address.trim(),
        emergency_contact: emergencyContact.trim(),
        additional_drivers_info: addlDrivers.map((d) => ({ name: d.name.trim(), license_number: d.license_number.trim() })),
        email: user ? undefined : stored,
      });
      toast.success("Contract signed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSigning(false);
    }
  };

  const uploadAddlLicense = async (i, file) => {
    if (!file) return;
    try {
      const stored = localStorage.getItem(`booking_email_${bookingId}`) || email;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", `additional_driver_${i + 1}`);
      if (!user) fd.append("email", stored);
      await api.post(`/bookings/${bookingId}/license`, fd);
      setAddlUploads((m) => ({ ...m, [i]: true }));
      toast.success(`Additional driver ${i + 1} license uploaded`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitReturn = async () => {
    if (!vehiclePhoto || !keyPhoto || !fuelPhoto) return toast.error("All three photos are required — vehicle, keys and fuel gauge");
    setReturnSubmitting(true);
    try {
      const stored = localStorage.getItem(`booking_email_${bookingId}`) || email;
      const pos = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { timeout: 8000, maximumAge: 60000 });
      });
      const fd = new FormData();
      fd.append("vehicle_photo", vehiclePhoto);
      fd.append("key_photo", keyPhoto);
      fd.append("fuel_photo", fuelPhoto);
      if (pos) {
        fd.append("gps_lat", String(pos.coords.latitude));
        fd.append("gps_lng", String(pos.coords.longitude));
        fd.append("gps_accuracy", String(pos.coords.accuracy));
      }
      if (!user) fd.append("email", stored);
      await api.post(`/bookings/${bookingId}/return-photos`, fd);
      toast.success("Return photos submitted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReturnSubmitting(false);
    }
  };

  if (error && !data)
    return (
      <div data-testid="contract-access-panel" className="max-w-md mx-auto px-6 pt-40 pb-24">
        <h1 className="font-display text-4xl tracking-tight">Rental Contract</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Enter the email used for this booking to view the contract.
        </p>
        <div className="mt-6 flex gap-3">
          <Input data-testid="contract-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark" />
          <Button data-testid="contract-load-button" onClick={() => load(email)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
            View
          </Button>
        </div>
      </div>
    );

  if (!data)
    return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading contract…</div>;

  const b = data.booking;
  const v = data.vehicle;

  return (
    <div data-testid="contract-page" className="contract-shell max-w-3xl mx-auto px-6 pt-32 pb-24">
      <style>{CONTRACT_PRINT_CSS}</style>
      <div className="no-print mb-8 flex items-center justify-between flex-wrap gap-4">
        <Link to={user?.role === "admin" ? "/admin" : "/account"} className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200">
          ← Back
        </Link>
        <Button data-testid="contract-print-button" variant="outline" onClick={() => window.print()} className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
          <Printer size={15} className="mr-2" /> Print / Save PDF
        </Button>
      </div>

      <div className="print-area bg-card border border-border rounded-2xl p-8 md:p-12">
        <div className="flex items-start justify-between border-b border-border pb-6">
          <div>
            <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="contract-logo" className="h-20 w-auto object-contain" />
            <div className="mt-1 text-xs text-muted-foreground font-ui space-y-0.5">
              <p>West Bay Street</p>
              <p>Nassau, Bahamas</p>
              <p>+1 (242) 813-1775</p>
              <p>bookings@easydrivecarrental.com</p>
              <p>www.easydrivecarrental.com</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Agreement</p>
            <p data-testid="contract-ref" className="gold-text font-ui font-semibold mt-1">{b.booking_ref}</p>
            <div className="mt-3 flex flex-col items-end">
              <Barcode value={String(v?.vin || b.booking_ref)} testid="contract-vin-barcode" />
              <p className="mt-1 text-[10px] font-ui text-muted-foreground">{v?.vin ? `VIN ${v.vin} — scan to match vehicle` : "Booking reference barcode"}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8 text-sm">
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Renter</p>
            <p className="font-semibold">{b.customer_name}</p>
            <p className="text-muted-foreground">{b.customer_email}</p>
            <p className="text-muted-foreground">{b.customer_phone}</p>
            {b.flight_number && <p className="text-muted-foreground">Flight: {b.flight_number}</p>}
            {b.loyalty_member_no && (
              <p data-testid="contract-loyalty-no" className="text-muted-foreground">Loyalty No. <span className="text-primary font-medium">{b.loyalty_member_no}</span>{b.loyalty_tier ? ` · ${b.loyalty_tier}` : ""}</p>
            )}
            {b.additional_drivers > 0 && (
              <p data-testid="contract-additional-drivers" className="text-muted-foreground">Additional drivers registered: {b.additional_drivers} — each must be 25+ and present a license at pickup</p>
            )}
          </div>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Vehicle</p>
            <p className="font-semibold">{v?.name || b.vehicle_name}</p>
            <p data-testid="contract-vehicle-specs" className="text-muted-foreground">{v?.category} · {v?.transmission} · {v?.seats} seats · Fits {v?.luggage ?? "—"} bags</p>
          </div>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Rental Period</p>
            <p>{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</p>
            <p className="text-muted-foreground">{b.days} {b.days === 1 ? "day" : "days"}</p>
            <p data-testid="contract-in-out-times" className="text-muted-foreground">Vehicle out: {b.pickup_time || "Time TBD"} · Vehicle in: {b.dropoff_time || "Time TBD"}</p>
            <p data-testid="contract-fuel-out" className="text-muted-foreground">Fuel out: {b.fuel_level || "Full"} — return at same level{v?.tank_capacity ? ` · Tank ${v.tank_capacity} gal` : ""}</p>
          </div>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Locations</p>
            <p>Pickup: {b.pickup_location}</p>
            <p className="text-muted-foreground">Drop-off: {b.dropoff_location}</p>
          </div>
        </div>

        <div data-testid="contract-checkin-qr" className="mt-8 border border-border rounded-xl p-5 flex items-center gap-5">
          <img src={`/api/bookings/${b.id}/checkin-qr.png`} alt="Express check-in QR" className="h-24 w-24 rounded-lg bg-white p-1.5 shrink-0" />
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-primary flex items-center gap-2"><Camera size={13} className="gold-text" /> Express check-in</p>
            <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">Scan with your phone to check in online before you land — or show it at the counter and our team will pull up your rental instantly.</p>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-xl border border-stone-200/80 shadow-[0_8px_30px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#C71E2E] via-[#8F1420] to-[#C71E2E]" />
          <div className="p-6">
            <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 border-b border-amber-200/60 pb-2 mb-1">Rental summary</p>
            <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
              <span className="text-stone-500">Rental rate</span>
              <span data-testid="contract-rate" className="font-mono text-stone-800">{fmtMoney(b.daily_rate)} / day @ {b.days} {b.days === 1 ? "day" : "days"}</span>
            </div>
            {b.baby_seat && (
              <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-stone-500">Baby car seat</span>
                <span className="font-mono text-stone-800">{b.days >= 12 ? <span className="text-emerald-600 font-medium">FREE — 12+ day perk</span> : fmtMoney(b.baby_seat_fee || 0)}</span>
              </div>
            )}
            {b.gps && (
              <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-stone-500">GPS navigation unit</span>
                <span className="font-mono text-stone-800">{fmtMoney(b.gps_fee || 0)}</span>
              </div>
            )}
            {b.roadside && (
              <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-stone-500">24/7 roadside assistance</span>
                <span className="font-mono text-stone-800">{fmtMoney(b.roadside_fee || 0)}</span>
              </div>
            )}
            {Number(b.vat_amount) > 0 && (
              <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-stone-500">VAT (10%)</span>
                <span className="font-mono text-stone-800">{fmtMoney(b.vat_amount)}</span>
              </div>
            )}
            {Number(b.processing_fee) > 0 && (
              <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-stone-500">Processing fee</span>
                <span className="font-mono text-stone-800">{fmtMoney(b.processing_fee)}</span>
              </div>
            )}
            {Number(b.upgrade_savings) > 0 && (
              <div data-testid="contract-upgrade-savings" className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
                <span className="text-emerald-600 font-medium">Complimentary upgrade — you save</span>
                <span className="font-mono text-emerald-600 font-semibold">{fmtMoney(b.upgrade_savings)}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-3 border-b border-stone-100 text-sm font-ui">
              <span className="text-stone-500">Refundable security deposit ({fmtMoney(b.deposit_amount || 150)})</span>
              <span className={b.deposit_status === "released" ? "text-emerald-600 font-medium" : "text-stone-600"}>
                {b.deposit_status === "released" ? "Released" : "Held — released at return"}
              </span>
            </div>
            <div className="flex items-center justify-between pt-4">
              <span className="font-ui text-sm font-semibold text-stone-900">Rental total ({b.days} days × {fmtMoney(b.daily_rate)})</span>
              <span data-testid="contract-total" className="gold-text font-display text-3xl tracking-tight">{fmtMoney(b.total)}</span>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">Terms & Conditions</p>
          <ol className="space-y-2.5 text-xs text-muted-foreground leading-relaxed list-decimal list-inside">
            {TERMS.map((t, i) => <li key={i}>{t}</li>)}
          </ol>
        </div>

        {(b.status === "confirmed" || b.status === "completed") && (
          <div data-testid="return-photos-panel" className="no-print mt-10 border border-primary/30 rounded-xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Camera size={13} className="gold-text" /> Vehicle return — required photos
            </p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              When returning the vehicle you MUST upload three photos: the vehicle's condition,
              where the keys were placed, and the fuel gauge (digital or analog) — the vehicle
              must be returned at the same fuel level it left with ({b.fuel_level || "Full"}).
              When you submit, share your live location if prompted — it records the vehicle's drop-off point for self rental return.
              Your rental is only closed — and your $150 deposit released — once all three are submitted.
            </p>
            {b.return_submitted ? (
              <div>
                <p data-testid="return-submitted-message" className="mt-4 text-sm text-emerald-600 font-ui flex items-center gap-2">
                  <ShieldCheck size={15} /> Return photos submitted — thank you!
                </p>
                {b.gps_return?.lat && (
                  <p data-testid="return-gps-recorded" className="mt-2 text-xs text-emerald-600 font-ui">Drop-off GPS location recorded ✓</p>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="font-ui text-xs text-muted-foreground">Vehicle condition photo</Label>
                  <Input data-testid="return-vehicle-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setVehiclePhoto(e.target.files?.[0] || null)} className="input-dark mt-1.5 file:text-primary file:mr-3" />
                </div>
                <div>
                  <Label className="font-ui text-xs text-muted-foreground">Key placement photo</Label>
                  <Input data-testid="return-key-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setKeyPhoto(e.target.files?.[0] || null)} className="input-dark mt-1.5 file:text-primary file:mr-3" />
                </div>
                <div>
                  <Label className="font-ui text-xs text-muted-foreground">Fuel gauge photo (digital or analog) — must show same level as pickup</Label>
                  <Input data-testid="return-fuel-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setFuelPhoto(e.target.files?.[0] || null)} className="input-dark mt-1.5 file:text-primary file:mr-3" />
                </div>
                <Button data-testid="return-submit-button" disabled={!vehiclePhoto || !keyPhoto || !fuelPhoto || returnSubmitting} onClick={submitReturn} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                  {returnSubmitting ? "Uploading…" : "Submit Return Photos"}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-10 border-t border-border pt-6">
          {b.contract_signed_at ? (
            <div data-testid="contract-signed-panel" className="flex items-center gap-3">
              <ShieldCheck size={20} className="gold-text" />
              <div>
                <p className="font-ui text-sm">Signed by <span className="gold-text font-semibold">{b.contract_signer}</span>{b.contract_initials && <span className="text-muted-foreground"> (initials: <span data-testid="contract-initials-display" className="text-foreground font-medium">{b.contract_initials}</span>)</span>}</p>
                {b.contract_signature_image && <img src={b.contract_signature_image} alt="Signature" data-testid="contract-signature-display" className="mt-1 h-14 w-auto rounded-md border border-border bg-white p-1" />}
                {b.contract_license_number && <p className="text-xs text-muted-foreground">License/Passport: {b.contract_license_number}</p>}
                {b.contract_address && <p className="text-xs text-muted-foreground">Address: {b.contract_address}</p>}
                {b.contract_emergency_contact && <p className="text-xs text-muted-foreground">Emergency contact: {b.contract_emergency_contact}</p>}
                {b.additional_drivers_info?.length > 0 && <p data-testid="contract-addl-drivers-display" className="text-xs text-muted-foreground">Additional drivers: {b.additional_drivers_info.map((d) => `${d.name} (Lic: ${d.license_number})`).join("; ")}</p>}
                <p className="text-xs text-muted-foreground">{new Date(b.contract_signed_at).toLocaleString()}</p>
                {b.contract_signed_ip && <p data-testid="contract-signed-ip" className="text-[10px] text-muted-foreground">E-signature audit: signed from IP {b.contract_signed_ip}</p>}
              </div>
            </div>
          ) : (
            <div data-testid="contract-sign-panel" className="no-print">
              <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 flex items-center gap-2">
                <PenLine size={13} className="gold-text" /> Sign this agreement
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input data-testid="contract-signer-input" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Full legal name (signature)" className="input-dark" />
                <Input data-testid="contract-initials-input" value={initials} onChange={(e) => setInitials(e.target.value)} placeholder="Your initials (e.g. JD)" maxLength={6} className="input-dark" />
                <Input data-testid="contract-license-number-input" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="Driver's license / passport number" className="input-dark" />
                <Input data-testid="contract-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Home address" className="input-dark" />
                <Input data-testid="contract-emergency-input" value={emergencyContact} onChange={(e) => setEmergencyContact(e.target.value)} placeholder="Emergency contact (name & phone)" className="input-dark" />
              </div>
              <div className="mt-4">
                <Label className="text-xs font-ui text-muted-foreground mb-1.5 block">Draw your signature below (required)</Label>
                <SignaturePad testid="contract-signature-pad" onChange={setSignatureImage} />
              </div>
              {(b.additional_drivers || 0) > 0 && (
                <div data-testid="contract-addl-drivers-panel" className="mt-5 border border-border rounded-xl p-4 space-y-4">
                  <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Additional drivers — registration & license upload required</p>
                  {addlDrivers.map((d, i) => (
                    <div key={i} className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input data-testid={`contract-addl-driver-name-${i + 1}`} value={d.name} onChange={(e) => setAddlDrivers((arr) => arr.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder={`Additional driver ${i + 1} — full legal name`} className="input-dark" />
                        <Input data-testid={`contract-addl-driver-license-${i + 1}`} value={d.license_number} onChange={(e) => setAddlDrivers((arr) => arr.map((x, j) => (j === i ? { ...x, license_number: e.target.value } : x)))} placeholder={`Additional driver ${i + 1} — license number`} className="input-dark" />
                      </div>
                      <div className="flex items-center gap-3">
                        <Input data-testid={`contract-addl-driver-upload-${i + 1}`} type="file" accept="image/*,application/pdf" onChange={(e) => uploadAddlLicense(i, e.target.files?.[0] || null)} className="input-dark file:text-primary file:mr-3" />
                        {addlUploads[i] && <span data-testid={`contract-addl-driver-uploaded-${i + 1}`} className="text-xs font-ui text-emerald-600 flex items-center gap-1 shrink-0"><ShieldCheck size={13} /> Uploaded</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button data-testid="contract-sign-button" disabled={signing} onClick={sign} className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                {signing ? "Signing…" : "Sign Contract"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
